import { localized, msg } from "@lit/localize";
import { renderErrorAlert, renderSuccessAlert } from "@whilecat/core/tasks/alerts.ts";
import { BaseTask } from "@whilecat/core/tasks/base-task.ts";
import { css, html, nothing } from "lit";
import "@whilecat/core/editors/code-area.ts";
import type { CodeArea } from "@whilecat/core/editors/code-area.ts";
import { defaultJavaEditorSettings } from "@whilecat/core/editors/code-editor-fabric.js";
import { type MarkLinesParams, MarkLinesParamsValidator } from "@whilecat/core/tasks/java/params/mark-lines-params.js";
import { arraysEqual } from "@whilecat/core/utils/arrays.ts";
import { requireNotNull } from "@whilecat/core/utils/validation.js";
import "@whilecat/core/buttons/run-button.ts";
import { customElement, state } from "lit/decorators.js";

@localized()
@customElement("mark-lines-task")
export class MarkLinesTask extends BaseTask<MarkLinesParams> {
    static styles = [
        super.styles,
        css`
          .code-line {
            display: flex;
            align-items: center;
            margin-bottom: 5px;
          }

          .line-checkbox {
            margin-right: 10px;
          }

          pre {
            margin: 0;
          }
        `,
    ];

    @state() private error: Boolean = false;
    @state() private solved: Boolean = false;
    @state() private failCounter: number = 0;

    private paramsValidator = new MarkLinesParamsValidator();

    static html(taskId: string, data: MarkLinesParams, completed: boolean, repetitionMode: boolean) {
        return html`
          <mark-lines-task
                  .taskId=${taskId}
                  .data=${data}
                  .completed="${completed}"
                  .repetitionMode="${repetitionMode}">
          </mark-lines-task>`;
    }

    override validateData() {
        this.paramsValidator.validate(this.data!);
    }

    override verify() {
        const editor = this.getCodeArea();
        const markedLines = editor.getMarkedLines();
        const correct = arraysEqual(this.data!.correctLines, markedLines);
        this.error = !correct;
        if (correct) {
            this.solved = true;
            editor.disable();
            this.publishCompleted();
        } else {
            this.failCounter++;
        }

        if (this.failCounter >= 3) {
            editor.enableShowAnswerButton();
        }
    }

    override showCorrectAnswer() {
        this.getCodeArea().markLines(this.data!.correctLines);
    }

    private getCodeArea(): CodeArea {
        return requireNotNull(this.shadowRoot!.getElementById("editor")) as CodeArea;
    }

    override render() {
        const settings = defaultJavaEditorSettings(this.data.code, false, true, false, () => this.showCorrectAnswer());

        return html`
          ${this.data!.content}
          <code-area id='editor' .settings="${settings}"></code-area>
          <run-button buttonText=${msg("Verify")}
                      ?disabled="${this.completed}"
                      .onClickFunction="${() => this.verify()}"
          ></run-button>
          ${this.error ? renderErrorAlert() : nothing}
          ${this.solved ? renderSuccessAlert(msg("Well done!")) : nothing}
        `;
    }
}
